import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge } from 'reactstrap';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';

const Section = ({ titleColor, title, children }) => (
  <div className="col-12">
    <h6 className={`${titleColor} mb-3 d-flex align-items-center`}>
      <AlertTriangle size={18} className="me-2" />
      {title}
    </h6>
    <div className="d-flex flex-wrap gap-2">{children}</div>
  </div>
);

const InventoryAlertsWidget = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const alerts = data?.alerts || {};
  const lowStockHighSpend = alerts.lowStockHighSpendParts || [];
  const belowMin = alerts.lowStockBelowMinThreshold || [];
  const endOfLife = alerts.endOfLifeAssets || [];

  const isEmpty = !lowStockHighSpend.length && !belowMin.length && !endOfLife.length;

  return (
    <ReportWidget
      title={t('business.inventory.alerts_notifications', 'Inventory Alerts & Notifications')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.alerts_notifications.empty', 'No inventory alerts right now.')}
      onRefresh={handleRefresh}
    >
      <div className="row g-3">
        {lowStockHighSpend.length > 0 && (
          <Section titleColor="text-warning" title={t('business.inventory.low_stock_alerts', 'Low Stock Alerts')}>
            {lowStockHighSpend.map((alert, index) => (
              <Badge key={index} color="warning" className="p-2">
                {alert}
              </Badge>
            ))}
          </Section>
        )}

        {belowMin.length > 0 && (
          <Section titleColor="text-danger" title={t('business.inventory.critical_stock_levels', 'Critical Stock Levels')}>
            {belowMin.map((item, index) => (
              <Badge key={index} color="danger" className="p-2">
                {item.partName}: {item.quantity}/{item.minimumStockLevel} ({t('shared.min', 'min')})
              </Badge>
            ))}
          </Section>
        )}

        {endOfLife.length > 0 && (
          <Section titleColor="text-info" title={t('business.inventory.asset_lifecycle_alerts', 'Asset Lifecycle Alerts')}>
            {endOfLife.map((alert, index) => (
              <Badge key={index} color="info" className="p-2">
                {alert}
              </Badge>
            ))}
          </Section>
        )}
      </div>
    </ReportWidget>
  );
};

export default InventoryAlertsWidget;
